import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Card, CardContent, Grid, Chip, LinearProgress, Button,
  IconButton, CircularProgress, Alert, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Accordion, AccordionSummary, AccordionDetails, Tooltip,
} from "@mui/material";
import ArrowBackRoundedIcon      from "@mui/icons-material/ArrowBackRounded";
import PlayArrowRoundedIcon      from "@mui/icons-material/PlayArrowRounded";
import PauseRoundedIcon          from "@mui/icons-material/PauseRounded";
import PlayCircleRoundedIcon     from "@mui/icons-material/PlayCircleRounded";
import EditRoundedIcon           from "@mui/icons-material/EditRounded";
import AddCardRoundedIcon        from "@mui/icons-material/AddCardRounded";
import LocalOfferRoundedIcon     from "@mui/icons-material/LocalOfferRounded";
import PercentRoundedIcon        from "@mui/icons-material/PercentRounded";
import CurrencyRupeeRoundedIcon  from "@mui/icons-material/CurrencyRupeeRounded";
import PriceCheckRoundedIcon     from "@mui/icons-material/PriceCheckRounded";
import ShoppingBagRoundedIcon    from "@mui/icons-material/ShoppingBagRounded";
import CardGiftcardRoundedIcon   from "@mui/icons-material/CardGiftcardRounded";
import PeopleRoundedIcon         from "@mui/icons-material/PeopleRounded";
import RepeatRoundedIcon         from "@mui/icons-material/RepeatRounded";
import EventRoundedIcon          from "@mui/icons-material/EventRounded";
import EventBusyRoundedIcon      from "@mui/icons-material/EventBusyRounded";
import PauseCircleRoundedIcon    from "@mui/icons-material/PauseCircleRounded";
import ExpandMoreRoundedIcon     from "@mui/icons-material/ExpandMoreRounded";
import TrendingUpRoundedIcon     from "@mui/icons-material/TrendingUpRounded";
import ReceiptLongRoundedIcon    from "@mui/icons-material/ReceiptLongRounded";
import LeaderboardRoundedIcon    from "@mui/icons-material/LeaderboardRounded";
import RouteRoundedIcon          from "@mui/icons-material/RouteRounded";
import { useNavigate, useParams } from "react-router-dom";
import { getData, postData } from "../../services/apiService";

// ── Brand palette ─────────────────────────────────────────────────────────
const T = {
  teal:    "#007C84", tealDk: "#016268", tealLt: "#E0F4F5",
  orange:  "#FF7900", orangeLt:"#FFF0E0",
  amber:   "#D97706", amberLt: "#FEF3C7",
  emerald: "#059669", emeraldLt:"#D1FAE5",
  rose:    "#E11D48", roseLt:  "#FFE4E6",
  indigo:  "#4F46E5", indigoLt:"#EEF2FF",
  violet:  "#7C3AED", violetLt:"#EDE9FE",
  bg:      "#F7FAFC", border:  "#E2E8F0",
  text:    "#0F172A", muted:   "#64748B",
};

const STATUS_META = {
  draft:            { color:"#475569", bg:"#F1F5F9", label:"Draft"            },
  active:           { color:T.emerald, bg:T.emeraldLt, label:"Active"         },
  paused:           { color:T.amber,   bg:T.amberLt,   label:"Paused"         },
  budget_exhausted: { color:T.rose,    bg:T.roseLt,    label:"Budget Exhausted"},
  expired:          { color:T.muted,   bg:"#F8FAFC",   label:"Expired"        },
};
const fmt = s => s?.replace(/_/g," ").replace(/\b\w/g,c=>c.toUpperCase()) || "—";
const money = v => (v==null||v===""?"—":`₹${Number(v).toLocaleString()}`);
const val   = v => (v==null||v===""?"—":v);

// ── Section card ──────────────────────────────────────────────────────────
const SCard = ({ accent=T.teal, children }) => (
  <Card sx={{ borderRadius:"16px",border:`1px solid ${T.border}`,boxShadow:"0 1px 4px rgba(15,23,42,0.06)",overflow:"hidden",width:"100%" }}>
    <Box sx={{ height:3,background:`linear-gradient(90deg,${accent},${accent}66)` }} />
    <CardContent sx={{ p:2.5,"&:last-child":{ pb:2.5 } }}>{children}</CardContent>
  </Card>
);

// ── Config tile ───────────────────────────────────────────────────────────
const ConfigTile = ({ icon, label, value, accent }) => (
  <Grid item xs={12} sm={6} md={4} lg={3}>
    <Box sx={{ p:1.5,borderRadius:"12px",border:`1px solid ${T.border}`,bgcolor:"#fff",display:"flex",gap:1.2,alignItems:"flex-start",
      "&:hover":{ borderColor:"#CBD5E1",boxShadow:"0 2px 8px rgba(0,0,0,0.04)" },transition:"all 0.12s" }}>
      <Box sx={{ width:32,height:32,borderRadius:"8px",bgcolor:accent ? `${accent}15` : "#F1F5F9",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
        {React.cloneElement(icon,{ sx:{ fontSize:16,color:accent||T.muted } })}
      </Box>
      <Box sx={{ minWidth:0,flex:1 }}>
        <Typography sx={{ fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.3px" }}>{label}</Typography>
        <Typography sx={{ fontSize:13,fontWeight:700,color:accent||T.text,wordBreak:"break-word",lineHeight:1.3,mt:0.25 }}>{value}</Typography>
      </Box>
    </Box>
  </Grid>
);

// ── Section header ────────────────────────────────────────────────────────
const SectionHead = ({ icon, iconBg, iconColor, title, subtitle }) => (
  <Box sx={{ display:"flex",alignItems:"center",gap:1.5,mb:2 }}>
    <Box sx={{ width:34,height:34,borderRadius:"9px",bgcolor:iconBg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
      {React.cloneElement(icon,{ sx:{ fontSize:17,color:iconColor } })}
    </Box>
    <Box>
      <Typography sx={{ fontSize:14,fontWeight:800,color:T.text,fontFamily:"'Plus Jakarta Sans',sans-serif" }}>{title}</Typography>
      {subtitle && <Typography sx={{ fontSize:12,color:T.muted,mt:0.1 }}>{subtitle}</Typography>}
    </Box>
  </Box>
);

// ── Main component ────────────────────────────────────────────────────────
export default function CampaignDetail() {
  const navigate = useNavigate();
  const { id }   = useParams();
  const [campaign,    setCampaign]    = useState(null);
  const [analytics,   setAnalytics]   = useState(null);
  const [redemptions, setRedemptions] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [busy,        setBusy]        = useState(false);
  const [error,       setError]       = useState(null);
  const [topupOpen,   setTopupOpen]   = useState(false);
  const [topupAmount, setTopupAmount] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [cRes,aRes,rRes] = await Promise.all([
        getData(`/api/dim/admin/campaigns/${id}`),
        getData(`/api/dim/admin/campaigns/${id}/analytics`),
        getData(`/api/dim/admin/campaigns/${id}/redemptions`,{ page:1,limit:10 }),
      ]);
      setCampaign(cRes?.result); setAnalytics(aRes?.result);
      setRedemptions(rRes?.result?.rows||[]);
    } catch(e) { setError(e.response?.data?.message||e.message||"Failed to load"); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const lifecycleAction = async (action, body={}) => {
    setBusy(true);
    try { await postData(`/api/dim/admin/campaigns/${id}/${action}`, body); await load(); }
    catch(e) { alert(e.response?.data?.message||`${action} failed`); }
    finally { setBusy(false); }
  };

  const submitTopup = async () => {
    const amt = Number(topupAmount);
    if (!amt||amt<=0) return;
    await lifecycleAction("budget-topup",{ amount:amt });
    setTopupOpen(false); setTopupAmount("");
  };

  if (loading) return <Box sx={{ display:"flex",justifyContent:"center",alignItems:"center",minHeight:"50vh" }}><CircularProgress sx={{ color:T.teal }} /></Box>;
  if (error)   return <Box sx={{ p:3 }}><Alert severity="error" sx={{ borderRadius:"12px" }}>{error}</Alert></Box>;
  if (!campaign) return null;

  const sm    = STATUS_META[campaign.status] || STATUS_META.draft;
  const used  = Number(campaign.budget_used);
  const cap   = Number(campaign.budget_cap);
  const pct   = cap > 0 ? Math.min(100,(used/cap)*100) : 0;
  const remaining = cap - used;
  const pctColor  = pct>=80 ? T.rose : pct>=50 ? T.amber : T.teal;

  return (
    <Box sx={{ bgcolor:T.bg, minHeight:"100vh", pb:4 }}>

      {/* ── Hero header ── */}
      <Box sx={{ background:`linear-gradient(135deg,${T.teal} 0%,${T.tealDk} 55%,#005559 100%)`,
        borderRadius:"20px", p:3, mb:3, boxShadow:"0 4px 20px rgba(0,124,132,0.2)" }}>
        <Box sx={{ display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:2 }}>
          <Box sx={{ display:"flex",alignItems:"center",gap:2 }}>
            <IconButton onClick={()=>navigate("/app/campaigns")}
              sx={{ bgcolor:"rgba(255,255,255,0.15)",border:"1px solid rgba(255,255,255,0.25)",borderRadius:"10px",color:"#fff","&:hover":{ bgcolor:"rgba(255,255,255,0.25)" } }}>
              <ArrowBackRoundedIcon sx={{ fontSize:18 }} />
            </IconButton>
            <Box sx={{ width:44,height:44,borderRadius:"12px",bgcolor:"rgba(255,255,255,0.18)",border:"1px solid rgba(255,255,255,0.25)",display:"flex",alignItems:"center",justifyContent:"center" }}>
              <LocalOfferRoundedIcon sx={{ fontSize:22,color:"#fff" }} />
            </Box>
            <Box>
              <Box sx={{ display:"flex",alignItems:"center",gap:1.5 }}>
                <Typography sx={{ fontSize:20,fontWeight:800,color:"#fff",fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                  {campaign.name}
                </Typography>
                <Chip size="small" label={sm.label}
                  sx={{ bgcolor:"rgba(255,255,255,0.2)",color:"#fff",fontWeight:700,fontSize:11,height:22,border:"1px solid rgba(255,255,255,0.3)" }} />
              </Box>
              <Typography sx={{ fontSize:12,color:"rgba(255,255,255,0.7)",mt:0.3 }}>
                Code: <Box component="code" sx={{ bgcolor:"rgba(255,255,255,0.15)",px:"4px",py:"1px",borderRadius:"4px",fontSize:11 }}>{campaign.code}</Box>
                {" "}· Type: {campaign.type} · Audience: {campaign.audience}
              </Typography>
            </Box>
          </Box>

          {/* Action buttons */}
          <Box sx={{ display:"flex",gap:1,flexWrap:"wrap" }}>
            {campaign.status === "draft" && (
              <>
                <Button variant="outlined" size="small" startIcon={<EditRoundedIcon sx={{ fontSize:14 }} />}
                  onClick={()=>navigate(`/app/campaigns/${id}/edit`)}
                  sx={{ borderRadius:"10px",textTransform:"none",fontWeight:700,color:"#fff",borderColor:"rgba(255,255,255,0.4)",bgcolor:"rgba(255,255,255,0.1)","&:hover":{ bgcolor:"rgba(255,255,255,0.2)" } }}>
                  Edit
                </Button>
                <Button variant="contained" disableElevation size="small" disabled={busy}
                  startIcon={<PlayArrowRoundedIcon sx={{ fontSize:14 }} />}
                  onClick={()=>lifecycleAction("activate")}
                  sx={{ borderRadius:"10px",textTransform:"none",fontWeight:700,bgcolor:T.emerald,"&:hover":{ bgcolor:"#047857" } }}>
                  Activate
                </Button>
              </>
            )}
            {campaign.status === "active" && (
              <Button variant="contained" disableElevation size="small" disabled={busy}
                startIcon={<PauseRoundedIcon sx={{ fontSize:14 }} />}
                onClick={()=>lifecycleAction("pause",{ reason:"manual pause from detail page" })}
                sx={{ borderRadius:"10px",textTransform:"none",fontWeight:700,bgcolor:T.orange,"&:hover":{ bgcolor:"#e06500" } }}>
                Pause
              </Button>
            )}
            {campaign.status === "paused" && (
              <Button variant="contained" disableElevation size="small" disabled={busy}
                startIcon={<PlayCircleRoundedIcon sx={{ fontSize:14 }} />}
                onClick={()=>lifecycleAction("resume")}
                sx={{ borderRadius:"10px",textTransform:"none",fontWeight:700,bgcolor:T.indigo,"&:hover":{ bgcolor:"#3730a3" } }}>
                Resume
              </Button>
            )}
            {["active","paused","budget_exhausted"].includes(campaign.status) && (
              <Button variant="outlined" size="small" disabled={busy}
                startIcon={<AddCardRoundedIcon sx={{ fontSize:14 }} />}
                onClick={()=>setTopupOpen(true)}
                sx={{ borderRadius:"10px",textTransform:"none",fontWeight:700,color:"#fff",borderColor:"rgba(255,255,255,0.4)",bgcolor:"rgba(255,255,255,0.1)","&:hover":{ bgcolor:"rgba(255,255,255,0.2)" } }}>
                Top Up Budget
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      <Grid container spacing={2}>
        {/* ── Budget card ── */}
        <Grid item xs={12}>
          <SCard accent={pctColor}>
            <SectionHead icon={<CurrencyRupeeRoundedIcon />} iconBg={T.tealLt} iconColor={T.teal}
              title="Budget" subtitle={`₹${used.toLocaleString()} used of ₹${cap.toLocaleString()}`} />

            <Box sx={{ display:"flex",alignItems:"center",gap:1.5,mb:1 }}>
              <LinearProgress variant="determinate" value={pct} sx={{ flex:1,height:10,borderRadius:5,bgcolor:"#F1F5F9",
                "& .MuiLinearProgress-bar":{ borderRadius:5,bgcolor:pctColor } }} />
              <Box sx={{ px:1.2,py:0.3,borderRadius:"8px",bgcolor:`${pctColor}15`,border:`1px solid ${pctColor}33`,flexShrink:0 }}>
                <Typography sx={{ fontSize:12,fontWeight:700,color:pctColor }}>{pct.toFixed(1)}% used</Typography>
              </Box>
            </Box>

            <Box sx={{ display:"grid", gridTemplateColumns:{ xs:"1fr 1fr", sm:"repeat(4,1fr)" }, gap:2, mt:2.5 }}>
              <Box sx={{ p:1.5,borderRadius:"12px",bgcolor:T.tealLt,border:`1px solid ${T.teal}22` }}>
                <Typography sx={{ fontSize:10,fontWeight:700,color:T.teal,textTransform:"uppercase",letterSpacing:"0.3px",mb:0.5 }}>Used</Typography>
                <Typography sx={{ fontSize:20,fontWeight:900,color:T.teal,fontFamily:"'Plus Jakarta Sans',sans-serif" }}>₹{used.toLocaleString()}</Typography>
              </Box>
              <Box sx={{ p:1.5,borderRadius:"12px",bgcolor:T.emeraldLt,border:`1px solid ${T.emerald}22` }}>
                <Typography sx={{ fontSize:10,fontWeight:700,color:T.emerald,textTransform:"uppercase",letterSpacing:"0.3px",mb:0.5 }}>Remaining</Typography>
                <Typography sx={{ fontSize:20,fontWeight:900,color:T.emerald,fontFamily:"'Plus Jakarta Sans',sans-serif" }}>₹{remaining.toLocaleString()}</Typography>
              </Box>
              <Box sx={{ p:1.5,borderRadius:"12px",bgcolor:T.amberLt,border:`1px solid ${T.amber}22` }}>
                <Typography sx={{ fontSize:10,fontWeight:700,color:T.amber,textTransform:"uppercase",letterSpacing:"0.3px",mb:0.5 }}>Redemptions</Typography>
                <Typography sx={{ fontSize:20,fontWeight:900,color:T.amber,fontFamily:"'Plus Jakarta Sans',sans-serif" }}>{analytics?.total_redemptions ?? 0}</Typography>
              </Box>
              <Box sx={{ p:1.5,borderRadius:"12px",bgcolor:T.violetLt,border:`1px solid ${T.violet}22` }}>
                <Typography sx={{ fontSize:10,fontWeight:700,color:T.violet,textTransform:"uppercase",letterSpacing:"0.3px",mb:0.5 }}>Avg Discount</Typography>
                <Typography sx={{ fontSize:20,fontWeight:900,color:T.violet,fontFamily:"'Plus Jakarta Sans',sans-serif" }}>{money(analytics?.avg_discount_per_redemption)}</Typography>
              </Box>
            </Box>
          </SCard>
        </Grid>

        {/* ── Configuration ── */}
        <Grid item xs={12}>
          <SCard accent={T.indigo}>
            <SectionHead icon={<PriceCheckRoundedIcon />} iconBg={T.indigoLt} iconColor={T.indigo}
              title="Configuration" subtitle="Campaign rules, limits, and validity window" />
            <Grid container spacing={1.5}>
              <ConfigTile icon={<PercentRoundedIcon />}      label="Discount Kind"      value={val(campaign.discount_kind)} />
              <ConfigTile icon={<CurrencyRupeeRoundedIcon />} label="Discount Value"    value={campaign.discount_value ? (campaign.discount_kind==="percent" ? `${Number(campaign.discount_value)}%` : money(campaign.discount_value)) : "—"} accent={T.indigo} />
              <ConfigTile icon={<PriceCheckRoundedIcon />}   label="Max Discount Cap"  value={money(campaign.max_discount_cap)} />
              <ConfigTile icon={<ShoppingBagRoundedIcon />}  label="Min Booking Value" value={money(campaign.min_booking_value)} />
              <ConfigTile icon={<CardGiftcardRoundedIcon />} label="Incentive Amount"  value={money(campaign.incentive_amount)} />
              <ConfigTile icon={<PeopleRoundedIcon />}       label="Max Per User"      value={val(campaign.max_per_user)} />
              <ConfigTile icon={<RepeatRoundedIcon />}       label="Max Total Uses"    value={val(campaign.max_total_uses)} />
              <ConfigTile icon={<EventRoundedIcon />}        label="Starts At"         value={new Date(campaign.start_at).toLocaleString()} accent={T.emerald} />
              <ConfigTile icon={<EventBusyRoundedIcon />}    label="Expires At"        value={new Date(campaign.expires_at).toLocaleString()} accent={T.rose} />
              {campaign.paused_reason && (
                <ConfigTile icon={<PauseCircleRoundedIcon />} label="Pause Reason" value={campaign.paused_reason} accent={T.amber} />
              )}
            </Grid>

            {/* Promo codes */}
            {Array.isArray(campaign.PromoCodes) && campaign.PromoCodes.length > 0 && (
              <Box sx={{ mt:2.5,pt:2,borderTop:`1px solid ${T.border}` }}>
                <Typography sx={{ fontSize:12,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.3px",mb:1 }}>Promo Codes</Typography>
                <Box sx={{ display:"flex",flexWrap:"wrap",gap:1 }}>
                  {campaign.PromoCodes.map(pc => (
                    <Box key={pc.promo_code_id} sx={{ px:1.2,py:0.5,bgcolor:T.indigoLt,borderRadius:"8px",border:`1px solid ${T.indigo}22`,display:"flex",alignItems:"center",gap:0.8 }}>
                      <LocalOfferRoundedIcon sx={{ fontSize:12,color:T.indigo }} />
                      <Typography sx={{ fontFamily:"monospace",fontSize:12,fontWeight:700,color:T.indigo }}>{pc.code}</Typography>
                      <Typography sx={{ fontSize:11,color:T.muted }}>{pc.uses_count}/{pc.max_uses??"∞"}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Rules JSON */}
            {campaign.rules_json && (
              <Box sx={{ mt:2 }}>
                <Accordion disableGutters elevation={0}
                  sx={{ border:`1px solid ${T.border}`,borderRadius:"12px !important","&:before":{ display:"none" } }}>
                  <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                    <Typography sx={{ fontSize:13,fontWeight:700,color:T.text }}>Rules JSON</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p:0 }}>
                    <Box component="pre" sx={{ fontSize:12,m:0,bgcolor:"#0F172A",color:"#E2E8F0",p:2,borderRadius:"0 0 12px 12px",overflow:"auto" }}>
                      {JSON.stringify(campaign.rules_json,null,2)}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Box>
            )}
          </SCard>
        </Grid>

        {/* ── Top redeemers ── */}
        {analytics?.top_users?.length > 0 && (
          <Grid item xs={12} md={6}>
            <SCard accent={T.violet}>
              <SectionHead icon={<LeaderboardRoundedIcon />} iconBg={T.violetLt} iconColor={T.violet}
                title="Top Redeemers" subtitle="Flag customers with suspicious redemption volume" />
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ "& .MuiTableCell-root":{ fontWeight:700,fontSize:11,textTransform:"uppercase",letterSpacing:"0.3px",color:T.muted,py:1,borderBottom:`2px solid ${T.border}` } }}>
                      <TableCell>User</TableCell>
                      <TableCell align="right">Redemptions</TableCell>
                      <TableCell align="right">Total ₹</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analytics.top_users.map((u,i) => (
                      <TableRow key={i} sx={{ "& .MuiTableCell-root":{ py:1,borderBottom:`1px solid ${T.border}` } }}>
                        <TableCell>
                          <Typography sx={{ fontSize:12,fontWeight:600,color:T.text }}>{u.user_type}#{u.user_id}</Typography>
                        </TableCell>
                        <TableCell align="right"><Typography sx={{ fontSize:12,fontWeight:700,color:T.text }}>{u.redemption_count}</Typography></TableCell>
                        <TableCell align="right"><Typography sx={{ fontSize:12,fontWeight:700,color:T.teal }}>{money(u.total_amount)}</Typography></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </SCard>
          </Grid>
        )}

        {/* ── Daily trend ── */}
        {analytics?.daily_trend?.length > 0 && (
          <Grid item xs={12} md={analytics?.top_users?.length > 0 ? 6 : 12}>
            <SCard accent={T.emerald}>
              <SectionHead icon={<TrendingUpRoundedIcon />} iconBg={T.emeraldLt} iconColor={T.emerald}
                title="Daily Trend" subtitle={`Last ${analytics.daily_trend.length} active days`} />
              <Box sx={{ display:"flex",flexDirection:"column",gap:1 }}>
                {analytics.daily_trend.map((d,i) => {
                  const max = Math.max(...analytics.daily_trend.map(x=>x.redemptions));
                  const p = max > 0 ? (d.redemptions/max)*100 : 0;
                  return (
                    <Box key={i}>
                      <Box sx={{ display:"flex",justifyContent:"space-between",mb:0.4 }}>
                        <Typography sx={{ fontSize:11,color:T.muted }}>{d.day?.toString().slice(0,10)}</Typography>
                        <Typography sx={{ fontSize:11,fontWeight:600,color:T.text }}>
                          {d.redemptions} · ₹{Number(d.amount||0).toFixed(0)}
                        </Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={p}
                        sx={{ height:5,borderRadius:3,bgcolor:"#F0F4F8",
                          "& .MuiLinearProgress-bar":{ borderRadius:3,bgcolor:T.emerald } }} />
                    </Box>
                  );
                })}
              </Box>
            </SCard>
          </Grid>
        )}

        {/* ── Route breakdown ── */}
        {analytics?.route_breakdown?.length > 0 && (
          <Grid item xs={12}>
            <SCard accent={T.amber}>
              <SectionHead icon={<RouteRoundedIcon />} iconBg={T.amberLt} iconColor={T.amber}
                title="Route Breakdown" subtitle="Redemptions per origin–destination pair" />
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ "& .MuiTableCell-root":{ fontWeight:700,fontSize:11,textTransform:"uppercase",letterSpacing:"0.3px",color:T.muted,py:1,borderBottom:`2px solid ${T.border}` } }}>
                      <TableCell>From</TableCell><TableCell>To</TableCell>
                      <TableCell align="right">Redemptions</TableCell><TableCell align="right">Total ₹</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analytics.route_breakdown.map((r,i) => (
                      <TableRow key={i} sx={{ "& .MuiTableCell-root":{ py:1,borderBottom:`1px solid ${T.border}` } }}>
                        <TableCell><Typography sx={{ fontSize:12,fontWeight:600 }}>{r.from_city}</Typography></TableCell>
                        <TableCell><Typography sx={{ fontSize:12,fontWeight:600 }}>{r.to_city}</Typography></TableCell>
                        <TableCell align="right"><Typography sx={{ fontSize:12,fontWeight:700 }}>{r.redemptions}</Typography></TableCell>
                        <TableCell align="right"><Typography sx={{ fontSize:12,fontWeight:700,color:T.teal }}>{money(r.amount)}</Typography></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </SCard>
          </Grid>
        )}

        {/* ── Recent redemptions ── */}
        <Grid item xs={12}>
          <SCard accent={T.teal}>
            <SectionHead icon={<ReceiptLongRoundedIcon />} iconBg={T.tealLt} iconColor={T.teal}
              title="Recent Redemptions" subtitle="Last 10 redemption events on this campaign" />
            {redemptions.length === 0 ? (
              <Typography sx={{ fontSize:13,color:T.muted }}>No redemptions yet on this campaign.</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ "& .MuiTableCell-root":{ fontWeight:700,fontSize:11,textTransform:"uppercase",letterSpacing:"0.3px",color:T.muted,py:1,borderBottom:`2px solid ${T.border}` } }}>
                      <TableCell>When</TableCell><TableCell>Event</TableCell>
                      <TableCell>User</TableCell><TableCell>Mapping</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {redemptions.map(r => (
                      <TableRow key={r.redemption_id} sx={{ "& .MuiTableCell-root":{ py:1.2,borderBottom:`1px solid ${T.border}` } }}>
                        <TableCell>
                          <Typography sx={{ fontSize:12,color:T.text }}>{new Date(r.createdAt).toLocaleDateString("en-IN")}</Typography>
                          <Typography sx={{ fontSize:11,color:T.muted }}>{new Date(r.createdAt).toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"})}</Typography>
                        </TableCell>
                        <TableCell><Chip size="small" label={r.event_kind} sx={{ fontSize:11,fontWeight:600,bgcolor:T.tealLt,color:T.teal,height:20 }} /></TableCell>
                        <TableCell><Typography sx={{ fontSize:12,fontWeight:600,color:T.text }}>{r.user_type}#{r.user_id}</Typography></TableCell>
                        <TableCell><Typography sx={{ fontSize:12,color:T.muted }}>{r.mapping_id ?? "—"}</Typography></TableCell>
                        <TableCell align="right"><Typography sx={{ fontSize:13,fontWeight:700,color:T.teal }}>{money(r.amount)}</Typography></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </SCard>
        </Grid>
      </Grid>

      {/* ── Top-up dialog ── */}
      <Dialog open={topupOpen} onClose={()=>setTopupOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx:{ borderRadius:"20px",overflow:"hidden" } }}>
        <Box sx={{ height:4,background:`linear-gradient(90deg,${T.teal},${T.tealLt})` }} />
        <DialogTitle sx={{ fontWeight:800,fontFamily:"'Plus Jakarta Sans',sans-serif",display:"flex",alignItems:"center",gap:1.5 }}>
          <Box sx={{ width:34,height:34,borderRadius:"9px",bgcolor:T.tealLt,display:"flex",alignItems:"center",justifyContent:"center" }}>
            <AddCardRoundedIcon sx={{ fontSize:17,color:T.teal }} />
          </Box>
          Top Up Campaign Budget
        </DialogTitle>
        <DialogContent sx={{ pt:1 }}>
          <Box sx={{ p:1.5,bgcolor:T.tealLt,borderRadius:"12px",border:`1px solid ${T.teal}33`,mb:2 }}>
            <Typography sx={{ fontSize:12,color:T.tealDk,lineHeight:1.5 }}>
              Adds to <strong>budget_cap</strong>. If the campaign was budget_exhausted and is within its window, it auto-resumes to active.
            </Typography>
          </Box>
          <TextField fullWidth label="Amount (₹)" type="number" autoFocus
            value={topupAmount} onChange={e=>setTopupAmount(e.target.value)}
            InputProps={{ sx:{ borderRadius:"12px" } }} />
        </DialogContent>
        <DialogActions sx={{ px:3,py:2.5,gap:1,borderTop:`1px solid ${T.border}` }}>
          <Button onClick={()=>setTopupOpen(false)} variant="outlined"
            sx={{ borderRadius:"10px",textTransform:"none",fontWeight:600,borderColor:T.border,color:T.muted,flex:1 }}>
            Cancel
          </Button>
          <Button variant="contained" disableElevation onClick={submitTopup} disabled={!topupAmount||busy}
            sx={{ borderRadius:"10px",textTransform:"none",fontWeight:700,bgcolor:T.teal,"&:hover":{ bgcolor:T.tealDk },flex:1 }}>
            Top Up
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
